import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { faFileContract } from "@fortawesome/free-solid-svg-icons";
import {
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Col,
    Content,
    ContentLoader,
    LoadingSpinner,
    Row,
    Table,
} from "@webtypen/react-components";
import { Page } from "../../components/Page";
import { ApiHelper, ConfirmHelper } from "../../helpers";

export const ContractRequestsList = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isAccepting, setIsAccepting] = useState(null);
    const [isDeclining, setIsDeclining] = useState(null);
    const [data, setData] = useState(null);

    const accept = async (id) => {
        if (!id) {
            return;
        }

        const status = await ConfirmHelper.confirm("Soll die Anfrage wirklich angenommen werden?", {
            confirmLabel: "Annehmen",
            confirmAttributes: { color: "success" },
        });
        if (!status) {
            return;
        }

        setIsAccepting(id);
        ApiHelper.request("/api/contracts/requests/accept", {
            _id: id,
        })
            .then((response) => {
                if (response.status === "success" && response.data && response.data._id && response.data.contract) {
                    navigate("/contracts/" + response.data.contract + "/edit");
                } else {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message && response.message.trim() !== ""
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                    setIsAccepting(null);
                    load();
                }
            })
            .catch((e) => {
                console.error(e);
                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ..."
                );
                setIsAccepting(null);
                load();
            });
    };

    const decline = async (id) => {
        if (!id) {
            return;
        }

        const status = await ConfirmHelper.confirm("Soll die Anfrage wirklich abgelehnt werden?", {
            confirmLabel: "Ablehnen",
            confirmAttributes: { color: "danger" },
        });
        if (!status) {
            return;
        }

        setIsDeclining(id);
        ApiHelper.request("/api/contracts/requests/reject", {
            _id: id,
        })
            .then((response) => {
                if (response.status !== "success") {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message && response.message.trim() !== ""
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                }
                setIsDeclining(null);
                load();
            })
            .catch((e) => {
                console.error(e);
                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ..."
                );
                setIsDeclining(null);
                load();
            });
    };

    const load = () => {
        setIsLoading(true);
        ApiHelper.request("/api/contracts/requests/list")
            .then((response) => {
                setData(
                    response.status === "success" && response.data && response.data.length > 0 ? response.data : null
                );
                setIsLoading(false);
            })
            .catch((e) => {
                console.error(e);
                setData(null);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <Page icon={faFileContract} title="Offene Auftragsanfrage">
            <Content>
                {isLoading ? (
                    <ContentLoader />
                ) : !data || data.length < 1 ? (
                    <div style={{ padding: 30, textAlign: "center", opacity: 0.7 }}>
                        Es gibt aktuell keine offenen Auftragsanfragen
                    </div>
                ) : (
                    data.map((entry, i) => (
                        <Card key={i} className="mb-3">
                            <CardHeader>
                                {entry.date ? moment(entry.date).format("DD.MM.YYYY") + " - " : "Fehlendes Datum - "}
                                {entry.customer_object && entry.customer_object.name
                                    ? entry.customer_object.name
                                    : "Unbekannter Kunde"}
                            </CardHeader>
                            <CardBody>
                                <div style={{ fontSize: 12 }}>
                                    Erstellt von{" "}
                                    {entry.user_object && entry.user_object._id
                                        ? entry.user_object.firstname + " " + entry.user_object.lastname
                                        : "Unbekannt"}{" "}
                                    am {moment(entry.created_at).format("DD.MM.YYYY")} um{" "}
                                    {moment(entry.created_at).format("HH:mm")} Uhr
                                </div>

                                <Row className="mt-2">
                                    <Col width={4}>
                                        <Card>
                                            <Table>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ width: 150 }}>Auftragsnr:</td>
                                                        <td>
                                                            {entry.customer_contract_number
                                                                ? entry.customer_contract_number
                                                                : "-"}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>Anzahl Schichten:</td>
                                                        <td>{entry.number_shifts ? entry.number_shifts : "-"}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Adresse:</td>
                                                        <td>
                                                            <div>{entry.address_city}</div>
                                                            <div>
                                                                {entry.address_zip} {entry.address_city}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Card>
                                    </Col>
                                    <Col width={8}>
                                        <div style={{ fontWeight: 500 }}>Nachricht:</div>
                                        {entry.message ? entry.message : <i>Es wurde keine Nachricht übermittelt</i>}
                                    </Col>
                                </Row>
                            </CardBody>
                            <CardFooter className="text-right">
                                <Button
                                    type="button"
                                    disabled={isAccepting || isDeclining}
                                    onClick={() => decline(entry._id)}
                                    sm
                                    danger
                                >
                                    {isDeclining === entry._id ? <LoadingSpinner /> : "Ablehnen"}
                                </Button>
                                <Button
                                    type="button"
                                    disabled={isAccepting || isDeclining}
                                    className="ml-2"
                                    onClick={() => accept(entry._id)}
                                    sm
                                    success
                                >
                                    {isAccepting === entry._id ? <LoadingSpinner /> : "Annehmen"}
                                </Button>
                            </CardFooter>
                        </Card>
                    ))
                )}
            </Content>
        </Page>
    );
};
